import React, { useCallback, useRef, useState } from 'react'

/**
 * Eine geteilte, leere Menge für alle Tage ohne Auswahl.
 *
 * `DayGroup` ist `React.memo`; bekäme jeder auswahllose Tag bei jeder
 * Auswahländerung eine **neue** leere Menge, rendert die ganze Seite neu
 * (~60 sichtbare Tage). Dieselbe Überlegung wie `EMPTY_TASKS` in
 * `upcomingTasks.utils.ts`.
 */
export const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>()

export interface UpcomingSelection {
  /** Alle ausgewählten Aufgaben — die Menge, auf die Menü und Kürzel wirken. */
  selectedIds: ReadonlySet<string>
  /** Die Zeile unter dem Tastatur-Cursor. Pfeile gehen von hier aus weiter. */
  focusedId: string | null
  /** Nur diese Zeile auswählen (Klick ohne Zusatztaste, Doppelklick, Details). */
  selectOnly: (taskId: string) => void
  /** Klick auf eine Zeile — wertet ⌘/Strg und ⇧ aus. */
  handleRowClick: (taskId: string, e: React.MouseEvent) => void
  /**
   * Pfeiltaste. `extend` ist ⇧: Der Bereich wächst vom Anker aus, statt die
   * Auswahl zu ersetzen. Gibt die neue Zeile zurück, damit der Aufrufer sie
   * ins Bild rollen kann — `null`, wenn es nicht weitergeht.
   */
  step: (direction: 'prev' | 'next', extend: boolean) => string | null
  /** Auswahl auf genau diese Zeile setzen (nach dem Löschen: die
   *  Nachfolgerin übernimmt). `null` leert. */
  moveTo: (taskId: string | null) => void
  /** Auswahl durch diese Zeilen ersetzen — nach ⌘D stehen die Kopien da. */
  replaceWith: (taskIds: string[]) => void
  clear: () => void
}

/**
 * Auswahl auf **Demnächst** — eine Menge, kein einzelner Eintrag
 * (Abnahme F-DN2, 22.09.2026).
 *
 * Die Seite kann `useTaskListInteraction` nicht benutzen: Der Hook hängt an
 * **einem** DOM-Container mit eigener Tastatur, *Demnächst* verteilt seine
 * Zeilen über Monate und Tage und hört am `window`. Die **Semantik** ist
 * absichtlich dieselbe — Klick, ⌘-Klick, ⇧-Klick, Pfeile, ⇧+Pfeile —, damit
 * sich die Seite nicht anders anfühlt als Kurz notiert oder ein Projekt.
 *
 * Der Anker ist eine **Kennung**, kein Index: Die Liste ändert sich unter der
 * Hand (Sync, Liegezeit nach dem Abhaken, Monat nachgeladen), ein gemerkter
 * Index zeigte danach auf eine fremde Zeile.
 */
export function useUpcomingSelection(flatTaskIds: string[]): UpcomingSelection {
  // Über einen Ref gelesen, nicht als Abhängigkeit: `flatTaskIds` ist bei
  // jedem Datenwechsel ein neues Array. Hingen die Griffe daran, wechselten
  // sie mit — und React.memo an DayGroup / TaskEntry griffe bei keinem.
  const idsRef = useRef(flatTaskIds)
  idsRef.current = flatTaskIds

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(EMPTY_SELECTION)
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const anchorRef = useRef<string | null>(null)
  // Spiegel der Auswahl, damit ⌘-Klick sie lesen kann, ohne einen zweiten
  // Setter in den Updater zu stecken (der läuft im Strict Mode doppelt).
  const selectedIdsRef = useRef<ReadonlySet<string>>(selectedIds)
  selectedIdsRef.current = selectedIds

  const rangeBetween = useCallback((fromId: string, toId: string): Set<string> => {
    const ids = idsRef.current
    const from = ids.indexOf(fromId)
    const to = ids.indexOf(toId)
    if (from === -1 || to === -1) return new Set([toId])
    const [lo, hi] = from <= to ? [from, to] : [to, from]
    return new Set(ids.slice(lo, hi + 1))
  }, [])

  const selectOnly = useCallback((taskId: string) => {
    setSelectedIds(new Set([taskId]))
    setFocusedId(taskId)
    anchorRef.current = taskId
  }, [])

  const clear = useCallback(() => {
    setSelectedIds(EMPTY_SELECTION)
    setFocusedId(null)
    anchorRef.current = null
  }, [])

  const moveTo = useCallback((taskId: string | null) => {
    if (taskId === null) {
      clear()
      return
    }
    selectOnly(taskId)
  }, [clear, selectOnly])

  const replaceWith = useCallback((taskIds: string[]) => {
    if (taskIds.length === 0) {
      clear()
      return
    }
    setSelectedIds(new Set(taskIds))
    const last = taskIds[taskIds.length - 1]!
    setFocusedId(last)
    anchorRef.current = last
  }, [clear])

  const handleRowClick = useCallback((taskId: string, e: React.MouseEvent) => {
    if (idsRef.current.indexOf(taskId) === -1) return
    const additive = e.metaKey || e.ctrlKey

    if (e.shiftKey && anchorRef.current !== null) {
      const range = rangeBetween(anchorRef.current, taskId)
      // ⇧ allein ersetzt die Auswahl durch den Bereich, ⌘+⇧ legt ihn dazu.
      setSelectedIds(additive ? new Set([...selectedIdsRef.current, ...range]) : range)
      setFocusedId(taskId)
      // Der Anker bleibt stehen: Ein zweites ⇧+Klick spannt vom selben Punkt.
      return
    }

    if (additive) {
      const next = new Set(selectedIdsRef.current)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      setSelectedIds(next)
      // Wird die Zeile abgewählt, wandert der Cursor auf die letzte, die
      // bleibt — sonst zeigte er auf etwas nicht mehr Ausgewähltes.
      setFocusedId(next.has(taskId) ? taskId : [...next].at(-1) ?? null)
      anchorRef.current = taskId
      return
    }

    selectOnly(taskId)
  }, [rangeBetween, selectOnly])

  const step = useCallback((direction: 'prev' | 'next', extend: boolean): string | null => {
    const ids = idsRef.current
    if (ids.length === 0) return null

    const current = focusedId ? ids.indexOf(focusedId) : -1
    const nextIdx = current === -1
      ? (direction === 'next' ? 0 : ids.length - 1)
      : Math.max(0, Math.min(ids.length - 1, current + (direction === 'next' ? 1 : -1)))
    const nextId = ids[nextIdx]
    if (!nextId) return null

    if (extend) {
      const anchor = anchorRef.current ?? focusedId ?? nextId
      anchorRef.current = anchor
      setSelectedIds(rangeBetween(anchor, nextId))
      setFocusedId(nextId)
    } else {
      selectOnly(nextId)
    }
    return nextId
  }, [focusedId, rangeBetween, selectOnly])

  return { selectedIds, focusedId, selectOnly, handleRowClick, step, moveTo, replaceWith, clear }
}
